import React from 'react';
import { Customer, Order } from '../types';

interface CustomerDetailsModalProps {
  customer: Customer;
  orders: Order[];
  onClose: () => void;
  onViewOrder: (order: Order) => void;
}

export const CustomerDetailsModal: React.FC<CustomerDetailsModalProps> = ({
  customer,
  orders,
  onClose,
  onViewOrder
}) => {
  const getStatusColor = (status: string) => {
    const colors = {
      'pending': { background: '#fff3cd', color: '#856404' },
      'assigned': { background: '#d4edda', color: '#155724' },
      'planted': { background: '#d1ecf1', color: '#0c5460' },
      'completed': { background: '#d1e7dd', color: '#0a3622' }
    };
    return colors[status as keyof typeof colors] || { background: '#e9ecef', color: '#495057' };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          borderRadius: '15px 15px 0 0'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
            Customer Details - {customer.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Customer Info */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
              Customer Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Email</div>
                <div style={{ color: '#333', marginTop: '0.2rem' }}>{customer.email}</div>
              </div>
              {customer.phone && (
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Phone</div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>{customer.phone}</div>
                </div>
              )}
              {customer.country && (
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Country</div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>{customer.country}</div>
                </div>
              )}
              <div>
                <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Customer Since</div>
                <div style={{ color: '#333', marginTop: '0.2rem' }}>
                  {new Date(customer.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
              Statistics
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
                  {customer.stats.totalOrders}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Orders</div>
              </div>
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
                  {customer.stats.totalTreesOrdered}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Trees Ordered</div>
              </div>
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
                  {customer.stats.treesPlanted}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Trees Planted</div>
              </div>
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#228B22' }}>
                  ${(customer.stats.totalAmountSpent / 100).toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Lifetime Value</div>
              </div>
            </div>
          </div>

          {/* Orders */}
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
              Order History ({orders.length})
            </h3>
            {orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                No orders found
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {orders.map((order) => (
                  <div
                    key={order._id}
                    style={{
                      border: '1px solid #eee',
                      padding: '1rem',
                      marginBottom: '0.75rem',
                      borderRadius: '8px',
                      background: '#f8f9fa'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>
                        Order #{order._id.slice(-6)}
                      </span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '15px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        ...getStatusColor(order.status)
                      }}>
                        {order.status}
                      </span>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '0.5rem',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '0.5rem'
                    }}>
                      <div>🌳 {order.trees || order.treesOrdered} trees</div>
                      <div>💰 {order.currency || 'USD'} {((order.totalAmount || order.amount || 0) / 100).toFixed(2)}</div>
                      <div>📅 {new Date(order.createdAt).toLocaleDateString()}</div>
                      {order.photos && order.photos.length > 0 && (
                        <div>📸 {order.photos.length} photos</div>
                      )}
                    </div>
                    <button
                      onClick={() => onViewOrder(order)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#228B22',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Close Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
